import { useJsApiLoader, type Libraries } from '@react-google-maps/api';
import { GOOGLE_LIBRARIES } from '@/constants/global';

/**
 * Single source of truth for loading the Google Maps JS API.
 *
 * The API must load EXACTLY ONCE per page. `@react-google-maps/api` builds its `<script>` URL from
 * the options below and dedupes by that URL + script id, so every call site must pass an IDENTICAL
 * options object. When two call sites disagree — a different `libraries` set or a different API
 * `key` — a second `<script>` is injected, the second load redefines `window.google.maps`, and a
 * `google.maps.marker.AdvancedMarkerElement` created against the first load fails its
 * `instanceof MapsApiMap` check when attached to a map from the second load. That surfaces as
 * `InvalidValueError: <gmp-advanced-marker>: Cannot set property "map" ...` (TEAM-13351).
 *
 * Do NOT call `useJsApiLoader({...})` directly anywhere else — use `useGoogleMapsLoader()` so the
 * whole app shares one load.
 */

// Superset of every library any map/autocomplete in the app needs. `marker` powers
// `google.maps.marker.AdvancedMarkerElement`; `places` powers the autocomplete/geocoding widgets.
export const GOOGLE_MAPS_LIBRARIES: Libraries = [GOOGLE_LIBRARIES.PLACES, GOOGLE_LIBRARIES.MARKER];

// Pin to a stable release channel so every load resolves the same `google.maps` build.
export const GOOGLE_MAPS_VERSION = 'quarterly';

// One explicit script id so the loader never injects a second `<script>`.
export const GOOGLE_MAPS_SCRIPT_ID = 'google-map-script';

/**
 * Resolve the Google Maps API key. Prefer the environment key (its HTTP-referrer restrictions are
 * configured for our production domains) and fall back to a CMS-provided key only when it is unset.
 * Choosing the key in one place is part of the single-load guarantee: different keys at different
 * call sites produce different `<script>` URLs and therefore duplicate loads.
 */
export const resolveGoogleMapsApiKey = (
  cmsMapKey?: string | null,
  envKey: string | undefined = process.env.NEXT_PUBLIC_GOOGLE_MAP_KEY,
): string => envKey ?? cmsMapKey ?? '';

export const GOOGLE_MAPS_LOADER_OPTIONS: Parameters<typeof useJsApiLoader>[0] = {
  id: GOOGLE_MAPS_SCRIPT_ID,
  googleMapsApiKey: resolveGoogleMapsApiKey(),
  libraries: GOOGLE_MAPS_LIBRARIES,
  version: GOOGLE_MAPS_VERSION,
};

/**
 * The single loader hook every map/autocomplete component must use. Returns the same
 * `{ isLoaded, loadError, url }` shape as `useJsApiLoader`.
 */
export const useGoogleMapsLoader = () => useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);
